import apiClient from './api';

const CACHE_KEY_HEADER = 'layout_header_config';
const CACHE_KEY_SIDEBAR = 'layout_sidebar_config';
const CACHE_KEY_FAVORITES = 'layout_favorite_pages';
const DEBOUNCE_MS = 500;

let saveFavoritesTimeout = null;

const layoutService = {
    getMyHeaderConfig: async () => {
        try {
            const response = await apiClient.get('/layouts/my-header/');
            if (response.status === 204) return null;
            const config = response.data;
            layoutService.cacheHeaderConfig(config);
            return config;
        } catch (error) {
            console.error('[layoutService] Error fetching header config:', error);
            return null;
        }
    },

    getHeaderConfigs: async () => {
        try {
            const response = await apiClient.get('/layouts/header-configs/');
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: error.message };
        }
    },

    createHeaderConfig: async (data) => {
        try {
            const response = await apiClient.post('/layouts/header-configs/', data);
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: error.response?.data || error.message };
        }
    },

    updateHeaderConfig: async (id, data) => {
        try {
            const response = await apiClient.put(`/layouts/header-configs/${id}/`, data);
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: error.response?.data || error.message };
        }
    },

    deleteHeaderConfig: async (id) => {
        try {
            await apiClient.delete(`/layouts/header-configs/${id}/`);
            return { success: true };
        } catch (error) {
            return { success: false, error: error.message };
        }
    },

    getMySidebarConfig: async () => {
        try {
            const response = await apiClient.get('/layouts/my-sidebar/');
            if (response.status === 204) {
                layoutService.cacheSidebarConfig(null);
                return null;
            }
            const config = response.data;
            layoutService.cacheSidebarConfig(config);
            return config;
        } catch (error) {
            console.error('[layoutService] Error fetching sidebar config:', error);
            return null;
        }
    },

    getSidebarConfigs: async () => {
        try {
            const response = await apiClient.get('/layouts/sidebar-configs/');
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: error.message };
        }
    },

    createSidebarConfig: async (data) => {
        try {
            const response = await apiClient.post('/layouts/sidebar-configs/', data);
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: error.response?.data || error.message };
        }
    },

    updateSidebarConfig: async (id, data) => {
        try {
            const response = await apiClient.put(`/layouts/sidebar-configs/${id}/`, data);
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: error.response?.data || error.message };
        }
    },

    deleteSidebarConfig: async (id) => {
        try {
            await apiClient.delete(`/layouts/sidebar-configs/${id}/`);
            return { success: true };
        } catch (error) {
            return { success: false, error: error.message };
        }
    },

    getCachedSidebarConfig: () => {
        try {
            const cached = localStorage.getItem(CACHE_KEY_SIDEBAR);
            return cached ? JSON.parse(cached) : null;
        } catch {
            return null;
        }
    },

    cacheSidebarConfig: (config) => {
        try {
            if (config) {
                localStorage.setItem(CACHE_KEY_SIDEBAR, JSON.stringify(config));
            } else {
                localStorage.removeItem(CACHE_KEY_SIDEBAR);
            }
        } catch {}
    },

    getFavorites: async () => {
        try {
            const response = await apiClient.get('/layouts/preferences/?key=favorites');
            const pages = response.data?.configuration?.pages || [];
            layoutService.cacheFavorites(pages);
            return pages;
        } catch (error) {
            console.error('[layoutService] Error fetching favorites:', error);
            return [];
        }
    },

    saveFavorites: (pages) => {
        layoutService.cacheFavorites(pages);
        if (saveFavoritesTimeout) clearTimeout(saveFavoritesTimeout);
        saveFavoritesTimeout = setTimeout(async () => {
            try {
                await apiClient.patch('/layouts/preferences/', {
                    preference_key: 'favorites',
                    configuration: { pages }
                });
            } catch (error) {
                console.error('[layoutService] Error saving favorites:', error);
            }
        }, DEBOUNCE_MS);
    },

    getCachedHeaderConfig: () => {
        try {
            const cached = localStorage.getItem(CACHE_KEY_HEADER);
            return cached ? JSON.parse(cached) : null;
        } catch {
            return null;
        }
    },

    cacheHeaderConfig: (config) => {
        try {
            if (config) {
                localStorage.setItem(CACHE_KEY_HEADER, JSON.stringify(config));
            } else {
                localStorage.removeItem(CACHE_KEY_HEADER);
            }
        } catch {}
    },

    getCachedFavorites: () => {
        try {
            const cached = localStorage.getItem(CACHE_KEY_FAVORITES);
            return cached ? JSON.parse(cached) : [];
        } catch {
            return [];
        }
    },

    cacheFavorites: (pages) => {
        try {
            localStorage.setItem(CACHE_KEY_FAVORITES, JSON.stringify(pages));
        } catch {}
    },

    clearCache: () => {
        localStorage.removeItem(CACHE_KEY_HEADER);
        localStorage.removeItem(CACHE_KEY_SIDEBAR);
        localStorage.removeItem(CACHE_KEY_FAVORITES);
    }
};

export default layoutService;
